// Acquisition-channel attribution: capture ?source=<slug> off a tracking link (generated
// in the admin panel), persist it across the pre-auth signup funnel, and stamp it onto the
// new business at registration. Pure localStorage/sessionStorage helper - no Supabase calls
// here, per the MVC rule that only model.js talks to Supabase (see logRegistrationEvent there).
//
// First-touch attribution: once a source is stored, a later ?source= (different or same)
// does NOT overwrite it until the stored one expires - the channel that FIRST brought the
// visitor gets credit, not whichever link they clicked last.

const VISITOR_KEY = "pointbunny_visitor_id";
const SOURCE_KEY = "pointbunny_source"; // JSON: { source, capturedAt }
const SOURCE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const FIRED_PREFIX = "pointbunny_src_evt_"; // sessionStorage, one flag per funnel stage

const isExpired = (capturedAt) => !capturedAt || Date.now() - capturedAt > SOURCE_TTL_MS;

// Long-lived anonymous id grouping this visitor's funnel events together.
export const getOrCreateVisitorId = function () {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch (_) {
    return null;
  }
};

export const getStoredSource = function () {
  try {
    const raw = localStorage.getItem(SOURCE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (isExpired(parsed.capturedAt)) {
      localStorage.removeItem(SOURCE_KEY);
      return null;
    }
    return parsed;
  } catch (_) {
    return null;
  }
};

export const clearStoredSource = function () {
  try {
    localStorage.removeItem(SOURCE_KEY);
  } catch (_) {
    // best-effort
  }
};

// Reads ?source= off the CURRENT page URL and stores it (first-touch - won't overwrite an
// unexpired existing value). Returns the raw slug found in this page's URL, or null - the
// caller uses this to know whether THIS load is the one that should fire link_opened /
// auto-open signup, as opposed to a later page view with no source param at all.
export const captureSourceFromUrl = function () {
  try {
    const urlSource = new URLSearchParams(window.location.search).get("source");
    if (!urlSource) return null;
    const existing = getStoredSource();
    if (!existing) {
      localStorage.setItem(SOURCE_KEY, JSON.stringify({ source: urlSource, capturedAt: Date.now() }));
    }
    return urlSource;
  } catch (_) {
    return null;
  }
};

export const hasFiredEvent = function (type) {
  try {
    return sessionStorage.getItem(FIRED_PREFIX + type) === "1";
  } catch (_) {
    return false;
  }
};

export const markEventFired = function (type) {
  try {
    sessionStorage.setItem(FIRED_PREFIX + type, "1");
  } catch (_) {
    // best-effort
  }
};
